import React, { useMemo } from "react";
import { useFormikContext } from "formik";
import classNames from "classnames";
import { mapValues } from "lodash";
import { FormRow } from "../forms/FormRow";
import { CustomSelect } from "../forms/CustomSelect/CustomSelect";
import { UserRole } from "../../common/types/userEntityAssignment";
import {
    GridTable,
    GridTableHeader,
    GridTableRow,
} from "../general/Tables/GridTable/GridTable";
import { EntityIcon } from "../entity/EntityIcon";
import { FormCheckbox } from "../forms/FormCheckbox/FormCheckbox";
import { Entity } from "../../common/types/entity";
import styles from "./MemberManagement.module.scss";
import { MemberPermissionsSchema, TeamFormProps } from "./types";
import { useAssignableWorkspaceLevelRoleOptions } from "./useAssignableWorkspaceLevelRoleOptions";
import { AssignableEntityRole } from "./lib";

export const ConfigurePermissionsFields: React.FC<TeamFormProps> = ({
    entities,
}) => {
    const workspaceLevelRoleOptions = useAssignableWorkspaceLevelRoleOptions();

    const formikContext = useFormikContext<MemberPermissionsSchema>();

    const selectedWorkspaceRole = formikContext.getFieldMeta("workspaceRole")
        .value as AssignableEntityRole;

    const preventSelectingIndividualEntities =
        selectedWorkspaceRole === UserRole.ADMIN;

    const businessEntities = useMemo(
        () => entities.filter((e) => e.isBusiness),
        [entities],
    );
    const personalEntities = useMemo(
        () => entities.filter((e) => !e.isBusiness),
        [entities],
    );

    const allBusinessIncluded = businessEntities.every(
        (e) => formikContext.values.entityAssignments[e.id],
    );
    const allPersonalIncluded = personalEntities.every(
        (e) => formikContext.values.entityAssignments[e.id],
    );

    const handleWorkspaceRoleChange = (role: AssignableEntityRole) => {
        formikContext.setFieldValue("workspaceRole", role);
        formikContext.setFieldValue(
            "entityAssignments",
            mapValues(
                formikContext.values.entityAssignments,
                (selected) => role === UserRole.ADMIN || selected,
            ),
        );
        formikContext.setFieldValue(
            "assignToNewEntities",
            role === UserRole.ADMIN || formikContext.values.assignToNewEntities,
        );
    };

    const toggleSelectAll = (entitiesInSection: Entity[]) => {
        const existingValue = {
            ...formikContext.values.entityAssignments,
        };

        const allSelected = entitiesInSection.every(
            (entity) => existingValue[entity.id],
        );

        for (const entity of entitiesInSection) {
            existingValue[entity.id] = !allSelected;
        }

        formikContext.setFieldValue("entityAssignments", existingValue);
    };

    return (
        <>
            <section className={styles.section}>
                <header className={styles.sectionHeader}>
                    <h6 className={styles.sectionTitle}>Assign role</h6>
                </header>

                <div>
                    <FormRow
                        className={styles.roleSelectGroup}
                        showErrorMessage={false}
                        fieldName="workspaceRole"
                    >
                        {(_helpers, { value }) => (
                            <CustomSelect
                                className={styles.roleSelect}
                                value={value}
                                onSelected={handleWorkspaceRoleChange}
                                dropdownKey="workspaceRole"
                                options={workspaceLevelRoleOptions}
                                fullWidthDropdown
                                size="sm"
                            />
                        )}
                    </FormRow>
                </div>
            </section>

            <section className={styles.section}>
                <header className={styles.sectionHeader}>
                    <h6 className={styles.sectionTitle}>Entities</h6>
                </header>

                <GridTable className={styles.table}>
                    <GridTableHeader
                        className={classNames(styles.row, styles.headerRow)}
                    >
                        <div>Business</div>
                        <div>
                            <FormCheckbox
                                className={styles.checkbox}
                                small
                                value="allIncluded"
                                isChecked={allBusinessIncluded}
                                handleChange={() =>
                                    toggleSelectAll(businessEntities)
                                }
                                disabled={preventSelectingIndividualEntities}
                            />
                        </div>
                    </GridTableHeader>

                    {businessEntities.map((entity) => (
                        <GridTableRow className={styles.row} key={entity.id}>
                            <div className={styles.itemCard}>
                                <EntityIcon entity={entity} size="xs" />{" "}
                                {entity.name}
                            </div>

                            <div>
                                <FormCheckbox
                                    small
                                    className={styles.checkbox}
                                    value={`entityAssignments.${entity.id}`}
                                    isChecked={
                                        formikContext.values.entityAssignments[
                                            entity.id
                                        ]
                                    }
                                    handleChange={() => {
                                        formikContext.setFieldValue(
                                            `entityAssignments.${entity.id}`,
                                            !formikContext.values
                                                .entityAssignments[entity.id],
                                        );
                                    }}
                                    disabled={
                                        preventSelectingIndividualEntities
                                    }
                                />
                            </div>
                        </GridTableRow>
                    ))}

                    <GridTableRow className={styles.row}>
                        <div className={styles.itemCard}>New entities</div>

                        <div>
                            <FormCheckbox
                                small
                                className={styles.checkbox}
                                value="assignToNewEntities"
                                isChecked={
                                    formikContext.values.assignToNewEntities
                                }
                                handleChange={() => {
                                    formikContext.setFieldValue(
                                        `assignToNewEntities`,
                                        !formikContext.values
                                            .assignToNewEntities,
                                    );
                                }}
                                disabled={preventSelectingIndividualEntities}
                            />
                        </div>
                    </GridTableRow>

                    {personalEntities.length > 0 ? (
                        <>
                            <GridTableHeader
                                className={classNames(
                                    styles.row,
                                    styles.headerRow,
                                )}
                            >
                                <div>Personal</div>
                                <div>
                                    <FormCheckbox
                                        className={styles.checkbox}
                                        small
                                        value="allIncluded"
                                        isChecked={allPersonalIncluded}
                                        handleChange={() =>
                                            toggleSelectAll(personalEntities)
                                        }
                                        disabled={
                                            preventSelectingIndividualEntities
                                        }
                                    />
                                </div>
                            </GridTableHeader>

                            {personalEntities.map((entity) => (
                                <GridTableRow
                                    className={styles.row}
                                    key={entity.id}
                                >
                                    <div className={styles.itemCard}>
                                        <EntityIcon entity={entity} size="xs" />{" "}
                                        {entity.name}
                                    </div>

                                    <div>
                                        <FormCheckbox
                                            small
                                            className={styles.checkbox}
                                            value={`entityAssignments.${entity.id}`}
                                            isChecked={
                                                formikContext.values
                                                    .entityAssignments[
                                                    entity.id
                                                ]
                                            }
                                            handleChange={() => {
                                                formikContext.setFieldValue(
                                                    `entityAssignments.${entity.id}`,
                                                    !formikContext.values
                                                        .entityAssignments[
                                                        entity.id
                                                    ],
                                                );
                                            }}
                                            disabled={
                                                preventSelectingIndividualEntities
                                            }
                                        />
                                    </div>
                                </GridTableRow>
                            ))}
                        </>
                    ) : null}
                </GridTable>
            </section>
        </>
    );
};
